import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ClassCard } from './class-card';
import { extractFirstUrl, type JoinState } from './join-utils';
import type { TactileVariant } from './tactile-card-styles';

interface ClassMosaicCardProps {
  classId: string;
  name: string;
  eventTitle?: string | null;
  eventStartTime: string;
  eventLocation?: string | null;
  imageUrl?: string | null;
  isPast: boolean;
  isToday: boolean;
  isLive: boolean;
  isSoon?: boolean;
  completedBlocks: number;
  totalBlocks: number;
}

const formatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function ClassMosaicCard({
  classId,
  name,
  eventTitle,
  eventStartTime,
  eventLocation,
  imageUrl,
  isPast,
  isToday,
  isLive,
  isSoon,
  completedBlocks,
  totalBlocks,
}: ClassMosaicCardProps) {
  const { t } = useTranslation('admin');
  const variant: TactileVariant = isToday || isLive ? 'dark' : 'light';

  const hasCompletedBlocks = Number.isFinite(completedBlocks) && completedBlocks > 0;
  const lastBlockIdx = hasCompletedBlocks ? Math.max(completedBlocks - 1, 0) : undefined;
  const linkTo = hasCompletedBlocks ? '/classes/$classId/block/$blockIdx' : '/classes/$classId';
  const linkParams = hasCompletedBlocks
    ? { classId, blockIdx: String(lastBlockIdx) }
    : { classId };

  const formattedDate = formatter.format(new Date(eventStartTime));
  const badgeLabel = isLive
    ? t('p2e.classes.page.badge.live', { defaultValue: 'Live' })
    : isToday
      ? t('p2e.classes.carousel.badge.today')
      : isPast
        ? t('p2e.classes.carousel.badge.previous', { defaultValue: 'Previous' })
        : t('p2e.classes.carousel.badge.next');

  const joinState: JoinState | null = isLive ? 'live' : isSoon ? 'soon' : null;
  const joinHref = extractFirstUrl(eventLocation);

  return (
    <Link
      to={linkTo}
      params={linkParams}
      className="outline-none focus-visible:ring-2 focus-visible:ring-rose-300 rounded-xl"
    >
      <ClassCard
        name={name}
        subtitle={eventTitle}
        formattedDate={formattedDate}
        imageUrl={imageUrl}
        badgeLabel={badgeLabel}
        variant={variant}
        completedBlocks={completedBlocks}
        totalBlocks={totalBlocks}
        joinState={joinState}
        joinHref={joinHref}
      />
    </Link>
  );
}
